import React from "react";
import { ImageIcon, Bookmark, ChevronRight } from "lucide-react";
import LocalDateTime from "services/date-converter";
import { useAccentColor } from "utils/accent-color";
import { formatEyebrowNumber } from "./knowledge-filters";

const StatusBadge = ({ article }) => {
  if (article.is_new) {
    return (
      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-danger_bg text-danger">
        New
      </span>
    );
  }
  if (article.viewer_state?.has_viewed) {
    return (
      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-primary_tint text-primary">
        Read
      </span>
    );
  }
  return null;
};

// One article line, used on the hub (recently added / filtered results) and on
// the chapter page. `showExcerpt` + `order` switch it to the chapter layout.
const ArticleRow = ({ article, onOpen, onToggleBookmark, showExcerpt = false, showChapter = true }) => {
  const accent = useAccentColor(article.chapter?.color_code);
  const saved = !!article.viewer_state?.is_bookmarked;

  return (
    <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => onOpen(article.id)}
        className="flex flex-1 min-w-0 items-center gap-3 sm:gap-4 text-left cursor-pointer"
      >
        <div className="flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-md overflow-hidden bg-surface_alt border border-border flex items-center justify-center">
          {article.banner_image ? (
            <img src={article.banner_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={16} className="text-text_muted" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="flex items-start gap-2 text-[13.5px] sm:text-sm font-semibold text-text_primary leading-snug">
            {showExcerpt && (
              <span className="flex-shrink-0 font-mono text-[10.5px] text-text_muted mt-[3px]">
                {formatEyebrowNumber(article.orderInChapter)}
              </span>
            )}
            <span className="line-clamp-2">{article.title}</span>
          </h3>
          {showExcerpt && article.excerpt && (
            <p className="text-[12px] text-text_secondary leading-relaxed line-clamp-2 mt-0.5">{article.excerpt}</p>
          )}
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 font-mono text-[10.5px] text-text_muted">
            {showChapter && (
              <span style={{ color: accent.text }}>CH {formatEyebrowNumber(article.chapterNumber)}</span>
            )}
            {showChapter && <span>·</span>}
            <LocalDateTime utcTime={article.published_at} type="date" />
            <span>·</span>
            <span>{article.engagement?.total_views || 0} views</span>
          </p>
        </div>
      </button>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="hidden sm:inline-flex">
          <StatusBadge article={article} />
        </span>
        <button
          type="button"
          onClick={() => onToggleBookmark(article.id)}
          aria-label={saved ? "Remove bookmark" : "Bookmark article"}
          onMouseDown={(e) => e.preventDefault()}
          className={`p-1.5 rounded-md cursor-pointer ${saved ? "text-primary" : "text-text_muted"}`}
        >
          <Bookmark size={15} className={saved ? "fill-current" : ""} />
        </button>
        {showExcerpt && <ChevronRight size={15} className="hidden sm:block text-text_muted" />}
      </div>
    </div>
  );
};

export default ArticleRow;
